/**
 * Optik „Glas" — persoenliche Wahl, das bisherige Design bleibt Standard.
 *
 * Betreiber 26.10.2026: Glas-Optik nach Vorbild „Glassy Dashboard" in den
 * GefAS-Farben, umschaltbar in den persoenlichen Einstellungen
 * (UserSettingsModal → „Darstellung"). Gespeichert wird die Wahl je Nutzer
 * in `profiles.settings.design` und zusaetzlich im Browser (localStorage),
 * damit schon das erste Bild nach dem Laden stimmt (mit der Profil-Id, der
 * er gehoert — ein anderes Konto am selben Geraet erbt ihn nicht). Zum Testen
 * schaltet auch die Adresse `?design=glas` bzw. `?design=classic` — nur fuer
 * diese Seite, nie gemerkt; ein Eintrag im Profil gewinnt nach dem Anmelden.
 *
 * Gezeichnet wird ueber `data-design="glas"` an <html> (Raum hinter der App,
 * Inhaltsfenster) und die Klassen `glass-scope` (Dashboard) und `glass-rail`
 * (Seitenleiste als Icon-Leiste), siehe src/design/glass.css. Fachlogik,
 * Daten und Navigation haengen nie daran.
 */
import { useLayoutEffect, useSyncExternalStore } from 'react';

export type DesignMode = 'classic' | 'glas';

export const DESIGN_STORAGE_KEY = 'osia.design';
/** Wem der im Browser gemerkte Wert gehoert (Profil-Id) — ein Wechsel des Kontos am selben Geraet erbt ihn nicht. */
export const DESIGN_USER_STORAGE_KEY = 'osia.design.user';
export const DESIGN_URL_PARAM = 'design';

/** Nur die zwei bekannten Werte; alles andere ist „keine Angabe". */
export const parseDesignMode = (value: unknown): DesignMode | null =>
    value === 'glas' ? 'glas' : value === 'classic' ? 'classic' : null;

/** `?design=glas|classic` aus dem Query-Teil der Adresse. */
export const designFromSearch = (search: string): DesignMode | null => {
    try {
        return parseDesignMode(new URLSearchParams(search).get(DESIGN_URL_PARAM));
    } catch {
        return null;
    }
};

let current: DesignMode = 'classic';
const listeners = new Set<() => void>();

const readStored = (): DesignMode | null => {
    try {
        return parseDesignMode(window.localStorage.getItem(DESIGN_STORAGE_KEY));
    } catch {
        return null;
    }
};

const readStoredUser = (): string | null => {
    try {
        return window.localStorage.getItem(DESIGN_USER_STORAGE_KEY);
    } catch {
        return null;
    }
};

const writeStored = (mode: DesignMode, userId: string | null): void => {
    try {
        if (mode === 'classic') {
            window.localStorage.removeItem(DESIGN_STORAGE_KEY);
            window.localStorage.removeItem(DESIGN_USER_STORAGE_KEY);
        } else {
            window.localStorage.setItem(DESIGN_STORAGE_KEY, mode);
            if (userId) window.localStorage.setItem(DESIGN_USER_STORAGE_KEY, userId);
            else window.localStorage.removeItem(DESIGN_USER_STORAGE_KEY);
        }
    } catch {
        /* Privater Modus / Speicher gesperrt: gilt dann nur bis zum Neuladen. */
    }
};

/** `?design=…` beim Laden: Vorschau nur fuer diese Seite, nie gemerkt. */
let vorschauAusAdresse: DesignMode | null = null;

/** `data-design="glas"` an <html>: der App-Rahmen (Raum, Fenster) haengt daran. */
const applyToDocument = (mode: DesignMode): void => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (mode === 'glas') root.dataset.design = 'glas';
    else delete root.dataset.design;
};

const emit = (): void => { listeners.forEach(fn => fn()); };

export const getDesignMode = (): DesignMode => current;

/** `userId`: wem der gemerkte Wert gehoert (null = noch niemand angemeldet). */
export const setDesignMode = (mode: DesignMode, userId: string | null = null): void => {
    writeStored(mode, userId);
    if (mode === current) return;
    current = mode;
    applyToDocument(mode);
    emit();
};

export const subscribeDesignMode = (fn: () => void): (() => void) => {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
};

let initialised = false;

/**
 * Einmal vor dem ersten Render (index.tsx): Adresse schlaegt gemerkten Wert,
 * der gemerkte Wert schlaegt den Standard. Die Adresse gilt nur fuer diese
 * Seite und wird NICHT gemerkt — sonst bliebe eine einmal geoeffnete
 * Vorschau-Adresse fuer immer haengen. Ein anderer Tab, der umschaltet,
 * zieht diesen ueber das `storage`-Ereignis mit.
 */
export const initDesignMode = (): void => {
    if (initialised || typeof window === 'undefined') return;
    initialised = true;
    const fromUrl = designFromSearch(window.location.search);
    vorschauAusAdresse = fromUrl;
    current = fromUrl ?? readStored() ?? 'classic';
    applyToDocument(current);
    window.addEventListener('storage', e => {
        if (e.key !== DESIGN_STORAGE_KEY) return;
        const next = parseDesignMode(e.newValue) ?? 'classic';
        if (next === current) return;
        current = next;
        applyToDocument(next);
        emit();
    });
};

/** `profiles.settings.design` — ungeprueft vom Server, deshalb ueber parseDesignMode. */
export const designFromSettings = (settings: unknown): DesignMode | null =>
    settings && typeof settings === 'object' ? parseDesignMode((settings as Record<string, unknown>).design) : null;

/** Zuletzt uebernommenes Profil: Konto UND Wert — gilt nie kontouebergreifend. */
let lastSeenProfile: { userId: string; mode: DesignMode | null } | null = null;

export interface ProfilDesignUmgebung {
    /** Profil-Id, der der im Browser gemerkte Wert gehoert. */
    gemerktFuer: string | null;
    /** Wert aus `?design=…`, mit dem die Seite geladen wurde (sonst null). */
    vorschau: DesignMode | null;
}

/** Was nach dem (Neu-)Laden des Profils gilt; `merken` = im Browser fuer das Konto ablegen. */
export interface ProfilDesignWahl {
    mode: DesignMode;
    merken: boolean;
}

/**
 * Welche Optik gilt, sobald das Profil (neu) geladen ist? null = nichts aendern.
 * - Ohne Anmeldung: nichts.
 * - Anderes Konto als zuletzt: dessen Profilwert. Ohne Eintrag gilt die
 *   Vorschau aus der Adresse (nur angezeigt, nie gemerkt — auch fuer ein
 *   Konto, das sich auf derselben Seite erst spaeter anmeldet), sonst der
 *   Browser-Wert, wenn er DIESEM Konto gehoert, sonst Klassisch (der
 *   Vorgaenger am selben Geraet vererbt nichts).
 * - Dasselbe Konto: nur einen GEAENDERTEN Profilwert. Haengt sich die Leiste
 *   neu ein, bevor das Profil nach einem Umschalten frisch geladen ist, kaeme
 *   sonst der alte Wert zurueck und die Optik spraenge kurz zurueck.
 */
export const takeProfileDesign = (
    userId: string | null,
    fromProfile: DesignMode | null,
    umgebung: ProfilDesignUmgebung,
): ProfilDesignWahl | null => {
    if (!userId) return null;
    if (lastSeenProfile?.userId !== userId) {
        lastSeenProfile = { userId, mode: fromProfile };
        if (fromProfile) return { mode: fromProfile, merken: true };
        if (umgebung.vorschau) return { mode: umgebung.vorschau, merken: false };
        if (umgebung.gemerktFuer === userId) return null;
        return { mode: 'classic', merken: true };
    }
    if (!fromProfile || fromProfile === lastSeenProfile.mode) return null;
    lastSeenProfile = { userId, mode: fromProfile };
    return { mode: fromProfile, merken: true };
};

/** Nur zeigen, nicht merken (Vorschau aus der Adresse). */
const zeigeDesign = (mode: DesignMode): void => {
    if (mode === current) return;
    current = mode;
    applyToDocument(mode);
    emit();
};

/**
 * Persoenliche Einstellung uebernehmen, sobald das Profil (neu) geladen ist.
 * Sie gilt fuer den Nutzer auf jedem Geraet und schlaegt den im Browser
 * gemerkten Wert; Regeln in takeProfileDesign.
 */
export const useProfileDesign = (userId: string | null | undefined, settings: unknown): void => {
    const fromProfile = designFromSettings(settings);
    const uid = userId ?? null;
    // Layout-Effekt: vor dem Zeichnen umschalten — sonst blitzt nach einem
    // Kontowechsel ein Bild in der Optik des Vorgaengers auf.
    useLayoutEffect(() => {
        const wahl = takeProfileDesign(uid, fromProfile, { gemerktFuer: readStoredUser(), vorschau: vorschauAusAdresse });
        if (!wahl) return;
        if (wahl.merken) setDesignMode(wahl.mode, uid);
        else zeigeDesign(wahl.mode);
    }, [uid, fromProfile]);
};

/** Nur fuer Tests. */
export const __resetProfileDesignForTests = (): void => { lastSeenProfile = null; };

export const useDesignMode = (): DesignMode =>
    useSyncExternalStore(subscribeDesignMode, getDesignMode, getDesignMode);

/** Kurzform fuer Komponenten: zeichne ich die Glas-Variante? */
export const useGlassDesign = (): boolean => useDesignMode() === 'glas';
